import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/cn";

type Variant = "primary" | "teal" | "ghost";
type Size = "md" | "sm";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-navy text-white hover:bg-navy-soft disabled:hover:bg-navy",
  teal: "bg-teal text-white hover:bg-teal-deep disabled:hover:bg-teal",
  ghost: "bg-surface text-ink border border-line hover:bg-line-soft disabled:hover:bg-surface",
};

const SIZES: Record<Size, string> = {
  md: "min-h-11 px-4 py-2.5 text-[14.5px] sm:min-h-0",
  sm: "min-h-11 px-3 py-1.5 text-[13px] sm:min-h-0",
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Shows a spinner and disables the button. */
  busy?: boolean;
  children: ReactNode;
}

export function Button({
  variant = "primary",
  size = "md",
  busy = false,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      // Guarding on `busy` here means callers cannot forget to block a
      // double-submit while a mutation is still in flight.
      disabled={disabled || busy}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-[9px] font-medium transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {busy && (
        <span
          aria-hidden="true"
          className="ai2-spin size-3.5 rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {/* Wrapped rather than rendered as a bare text child. A text node sitting
          next to the conditional spinner above is what browser translation
          rewrites into a <font> element, after which React's insertBefore for
          the spinner targets a node that is no longer its child and throws. */}
      <span>{children}</span>
    </button>
  );
}
